import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Container,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Event as EventIcon,
  Today as TodayIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { Appointment, CreateAppointmentRequest, UpdateAppointmentRequest } from '../types/Appointment';
import { Patient } from '../types/Patient';
import { appointmentService } from '../services/appointmentService';
import { patientService } from '../services/patientService';

const AppointmentsPage: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [formData, setFormData] = useState({
    patientId: 0,
    appointmentDate: '',
    appointmentTime: '',
    doctorName: 'Puneet Bharadwaj',
    department: 'General Medicine',
    appointmentType: '',
    reason: '',
    notes: '',
    status: 'Scheduled',
    doctorNotes: '',
    consultationFee: 0,
  });

  const departments = ['Cardiology', 'Neurology', 'Pediatrics', 'Orthopedics', 'Emergency', 'General Medicine'];
  const appointmentTypes = ['Consultation', 'Follow-up', 'Emergency', 'Routine Check-up', 'Specialist Visit'];
  const statusOptions = ['Scheduled', 'Confirmed', 'In Progress', 'Completed', 'Cancelled', 'Rescheduled'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [appointmentsData, patientsData] = await Promise.all([
        appointmentService.getAllAppointments(),
        patientService.getAllPatients(),
      ]);
      setAppointments(appointmentsData);
      setPatients(patientsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setError('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case 'Scheduled': return 'info';
      case 'Confirmed': return 'primary';
      case 'In Progress': return 'warning';
      case 'Completed': return 'success';
      case 'Cancelled': return 'error';
      case 'Rescheduled': return 'secondary';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleOpenDialog = (appointment?: Appointment) => {
    if (appointment) {
      setEditingAppointment(appointment);
      setFormData({
        patientId: appointment.patientId,
        appointmentDate: appointment.appointmentDate ? appointment.appointmentDate.split('T')[0] : '',
        appointmentTime: appointment.appointmentTime,
        doctorName: appointment.doctorName,
        department: appointment.department,
        appointmentType: appointment.appointmentType || '',
        reason: appointment.reason || '',
        notes: appointment.notes || '',
        status: appointment.status,
        doctorNotes: appointment.doctorNotes || '',
        consultationFee: appointment.consultationFee || 0,
      });
    } else {
      setEditingAppointment(null);
      setFormData({
        patientId: 0,
        appointmentDate: '',
        appointmentTime: '',
        doctorName: 'Puneet Bharadwaj',
        department: 'General Medicine',
        appointmentType: '',
        reason: '',
        notes: '',
        status: 'Scheduled',
        doctorNotes: '',
        consultationFee: 0,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
  };

  const handleSubmit = async () => {
    try {
      if (editingAppointment) {
        const updateData: UpdateAppointmentRequest = {
          doctorName: formData.doctorName,
          department: formData.department,
          appointmentDate: formData.appointmentDate,
          appointmentTime: formData.appointmentTime,
          status: formData.status,
          appointmentType: formData.appointmentType,
          reason: formData.reason,
          notes: formData.notes,
          doctorNotes: formData.doctorNotes,
          consultationFee: formData.consultationFee,
        };
        await appointmentService.updateAppointment(editingAppointment.appointmentId, updateData);
      } else {
        const createData: CreateAppointmentRequest = {
          patientId: formData.patientId,
          doctorName: formData.doctorName,
          department: formData.department,
          appointmentDate: formData.appointmentDate,
          appointmentTime: formData.appointmentTime,
          appointmentType: formData.appointmentType,
          reason: formData.reason,
          notes: formData.notes,
          consultationFee: formData.consultationFee,
        };
        await appointmentService.createAppointment(createData);
      }
      await fetchData();
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save appointment:', error);
      setError('Failed to save appointment');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      try {
        await appointmentService.deleteAppointment(id);
        await fetchData();
      } catch (error) {
        console.error('Failed to delete appointment:', error);
        setError('Failed to delete appointment');
      }
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      await fetchData();
      return;
    }
    
    try {
      setLoading(true);
      const data = await appointmentService.getAllAppointments();
      const filtered = data.filter(appointment => 
        appointment.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        appointment.doctorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        appointment.department.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setAppointments(filtered);
    } catch (error) {
      console.error('Failed to search appointments:', error);
      setError('Failed to search appointments');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        sx={{ 
          background: 'linear-gradient(135deg, #f8fffe 0%, #e0f2f1 100%)',
          borderRadius: 3,
          m: 2
        }}
      >
        <CircularProgress sx={{ color: '#2C7A9C' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ 
      py: 3,
      background: 'transparent',
      minHeight: '100vh'
    }}>
      {/* Header Section */}
      <Card 
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
          color: 'white',
          borderRadius: 4,
          mb: 3,
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <EventIcon sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                  My Appointments
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, color: 'white' }}>
                  Manage and track all patient appointments
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.3)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)',
                }
              }}
            >
              Schedule Appointment
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search Section */}
      <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: '1px solid #6AB5D1' }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by patient name, doctor, or department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: '#2C7A9C', mr: 1 }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f8fafc',
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#2C7A9C',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2C7A9C',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                onClick={() => setSearchTerm('')}
                fullWidth
                sx={{
                  background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
                  borderRadius: 2,
                  py: 1.5,
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card 
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #e8f4f8 0%, #c5dfe8 100%)',
              border: '1px solid #6AB5D1',
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <TodayIcon sx={{ fontSize: 40, color: '#2C7A9C', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1E5A73', mb: 1 }}>
                {appointments.filter(apt => 
                  new Date(apt.appointmentDate).toDateString() === new Date().toDateString()
                ).length}
              </Typography>
              <Typography variant="body1" sx={{ color: '#4a6a6a' }}>
                Today's Appointments
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card 
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #e8f4f8 0%, #d1fae5 100%)',
              border: '1px solid #6ee7b7',
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <ScheduleIcon sx={{ fontSize: 40, color: '#059669', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#047857', mb: 1 }}>
                {appointments.filter(apt => apt.status === 'Scheduled').length}
              </Typography>
              <Typography variant="body1" sx={{ color: '#4a6a6a' }}>
                Scheduled
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card 
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              border: '1px solid #fbbf24',
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <EventIcon sx={{ fontSize: 40, color: '#d97706', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#92400e', mb: 1 }}>
                {appointments.length}
              </Typography>
              <Typography variant="body1" sx={{ color: '#64748b' }}>
                Total Appointments
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Appointments Table */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #6AB5D1', overflow: 'hidden' }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
                  '& th': { 
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    borderBottom: 'none',
                  }
                }}>
                  <TableCell>Patient</TableCell>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Doctor</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {appointments.map((appointment, index) => (
                  <TableRow 
                    key={appointment.appointmentId}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                      '&:hover': {
                        backgroundColor: '#e8f4f8',
                        transform: 'scale(1.01)',
                        transition: 'all 0.2s ease',
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                      {appointment.patientName}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2C7A9C' }}>
                          {formatDate(appointment.appointmentDate)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          {formatTime(appointment.appointmentTime)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      Dr. {appointment.doctorName}
                    </TableCell>
                    <TableCell>{appointment.appointmentType || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={appointment.status}
                        color={getStatusColor(appointment.status)}
                        size="small"
                        sx={{ 
                          fontWeight: 600,
                          borderRadius: 2,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(appointment)}
                            sx={{ 
                              color: '#2C7A9C',
                              '&:hover': { backgroundColor: '#e8f4f8' }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(appointment.appointmentId)}
                            sx={{ 
                              color: '#ef4444',
                              '&:hover': { backgroundColor: '#fef2f2' }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {appointments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                      <EventIcon sx={{ fontSize: 60, color: '#cbd5e1', mb: 2 }} />
                      <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                        No appointments found
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                        Schedule your first appointment to get started
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            border: '1px solid #6AB5D1',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
          color: 'white',
          fontWeight: 600,
        }}>
          {editingAppointment ? 'Edit Appointment' : 'Schedule New Appointment'}
        </DialogTitle>
        <DialogContent sx={{ p: 3, backgroundColor: '#fafafa' }}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Patient</InputLabel>
                <Select
                  value={formData.patientId}
                  onChange={(e) => setFormData({ ...formData, patientId: Number(e.target.value) })}
                  label="Patient"
                >
                  {patients.map((patient) => (
                    <MenuItem key={patient.patientId} value={patient.patientId}>
                      {patient.fullName} - {patient.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Appointment Date"
                type="date"
                value={formData.appointmentDate}
                onChange={(e) => setFormData({ ...formData, appointmentDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Appointment Time"
                type="time"
                value={formData.appointmentTime}
                onChange={(e) => setFormData({ ...formData, appointmentTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Appointment Type</InputLabel>
                <Select
                  value={formData.appointmentType}
                  onChange={(e) => setFormData({ ...formData, appointmentType: e.target.value })}
                  label="Appointment Type"
                >
                  {appointmentTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  label="Status"
                >
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes or instructions..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, backgroundColor: '#fafafa' }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ 
              borderColor: '#cbd5e1',
              color: '#64748b',
              '&:hover': { borderColor: '#94a3b8' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
              fontWeight: 600,
            }}
          >
            {editingAppointment ? 'Update' : 'Schedule'} Appointment
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AppointmentsPage;